import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import interviewCreated, {
	type InterviewCreatedAction,
} from '@Application/Beneficiaries/Interviews/Actions/interviewCreated';
import interviewCreationFailed, {
	type InterviewCreationFailedAction,
} from '@Application/Beneficiaries/Interviews/Actions/interviewCreationFailed';
import type { InterviewPayload } from '@Application/Beneficiaries/Interviews/Actions/interviewsFetched';
import type CreateInterview from '@Application/Beneficiaries/Interviews/Commands/CreateInterview';
import type CreateTextInterview from '@Application/Beneficiaries/Interviews/Commands/CreateTextInterview';
import { post } from '@Domain/Api';
import ApiError from '@Infra/FetchLib/ApiError';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import type * as React from 'react';

type Actions = InterviewCreatedAction | InterviewCreationFailedAction;

/**
 * Called by the transfer handler (through its own `dispatch`) and by the text entry panel, so the store
 * reflects the server-created interview instead of leaving the local list to drift silently from the
 * server. A rejeu on the same `clientReference` answers the same payload: this function never distinguishes a
 * first creation from a replay.
 */
const createInterview = (command: CreateInterview | CreateTextInterview) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const payload = await post<InterviewPayload>(
				`${buildSubjectApiPath(command.facilityId, command.subject)}/interviews`,
				command.toJSON(),
			);

			const action = interviewCreated(payload, command.subject.typeKey, command.subject.subjectId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = interviewCreationFailed(
				extractMessages(error),
				error instanceof ApiError ? error.status : null,
			);
			dispatch(action);
			return Promise.reject(action);
		}
	};
};

export default createInterview;
